import React from 'react';
import Popper from '@mui/material/Popper';
import { styled } from '@mui/material/styles';
import Autocomplete, { autocompleteClasses } from '@mui/material/Autocomplete';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import {
  CascadeOption,
  getSelectedOptions,
} from '@/components/form/inputs/CascadeSelect';
import CascadeSelectOptions from '@/components/form/inputs/CascadeSelectOptions';
import Input from './Input';
import { useClintMediaQuery } from '@/utils/hooks';
import { Option } from '@/components/form/inputs/CascadeSelect';
import styles from '@/components/form/inputs/CascadeSelect.module.scss';
import { trackClickEvent } from '@/utils/site-track';

const SELECT_SELECTED = 'selected';
const SELECT_SHOW = 'show-option';

const DEFAULT_SELECT_OPTION = {
  parent: { ele: null, top: 0 },
  child: { ele: null, top: 0 },
};

interface SelectOption {
  parent: { ele: null | HTMLElement; top: number };
  child: { ele: null | HTMLElement; top: number };
}

const CitySearchOptions = (props: {
  options: CascadeOption[];
  onChange: (selectValue: Option, selectOptions: Option[]) => void;
  value?: Option;
  isHalfWidth?: boolean;
  className?: string;
  sx?: React.CSSProperties;
  inputSx?: React.CSSProperties;
  label?: string;
  placeholder?: string;
  error?: boolean;
  helperText?: string;
  optionsClassName?: string;
  handleMobileSearch?: () => void;
  pageUniqueId?: string;
  labelColor?: string;
  dataEleId?: string;
  dataEleGroup?: string;
  dataObserverId?: string;
}) => {
  const {
    value,
    onChange,
    options,
    isHalfWidth = false,
    className = '',
    label = '',
    labelColor = 'text-black',
    placeholder = '',
    sx,
    error,
    helperText,
    optionsClassName = '',
    handleMobileSearch,
    pageUniqueId = '',
    dataEleId,
    dataEleGroup,
    dataObserverId,
  } = props;
  const [autoCompleteOpen, setAutoCompleteOpen] = React.useState(false);
  const [selectOption, setSelectOption] = React.useState<SelectOption>(
    DEFAULT_SELECT_OPTION,
  );
  const [endAdornment, setEndAdornment] = React.useState(
    <ArrowDropDownIcon sx={{ color: '#79747e' }} />,
  );
  const selectMenuRef = React.useRef<HTMLUListElement>(null);
  const inputRef = React.useRef<HTMLInputElement | undefined>(undefined);
  const [searchValue, setSearchValue] = React.useState('');
  const isMobile = useClintMediaQuery();

  const styledPopper = styled(Popper)({
    paddingTop: '4px',
    [`.${autocompleteClasses.listbox} .${autocompleteClasses.option}.${autocompleteClasses.focused}`]:
      {
        color: '#1861bf',
        background: 'rgba(24, 97, 191, 0.1)',
      },
  });

  const cityOptions = React.useMemo(() => {
    if (Array.isArray(options) && options.length > 0) {
      const myOptions: Option[] = [];
      options.map((item) => {
        const id = item.id;
        const children = ('children' in item && item?.children) ?? [];
        if (id && id !== 'hot_cities' && children && children.length > 0) {
          children?.map(({ id, name, pinyin }) => {
            myOptions.push({
              id,
              name,
              pinyin,
              searchPinyin: pinyin?.replace(/[0-9]/g, ''),
            });
          });
        }
      });
      return myOptions;
    }
    return [];
  }, [options]);

  const search = React.useCallback(
    (value: string): Option[] => {
      const lowerCaseValue = value.replace(/[\x20-\x40]/g, '').toLowerCase();
      if (lowerCaseValue.length === 0) return [];
      const result: { id: string; name: string; pinyin: string }[] = [];
      const category = lowerCaseValue[0].match(/[a-z]/)
        ? 'searchPinyin'
        : 'name';
      for (const obj of cityOptions) {
        if (obj[category]?.startsWith(lowerCaseValue)) {
          result.push({ id: obj.id, name: obj.name, pinyin: obj.pinyin || '' });
          if (result.length >= 5) return result;
        }
      }
      return result;
    },
    [cityOptions],
  );

  const searchOptions: Option[] = React.useMemo(() => {
    return search(searchValue);
  }, [searchValue]);

  const handleSelectOption = (value: SelectOption) => {
    const {
      parent: { ele: parentELe },
      child: { ele: currentEle },
    } = value;
    setSelectOption((prev) => {
      const {
        parent: { ele: prevParentEle },
        child: { ele: prevChildEle },
      } = prev;
      if (prevParentEle && prevParentEle !== parentELe) {
        prevParentEle.classList.remove(SELECT_SELECTED);
        prevParentEle.firstElementChild?.classList.remove(SELECT_SHOW);
      }
      if (prevChildEle && prevChildEle !== currentEle) {
        prevChildEle?.classList.remove(SELECT_SELECTED);
      }
      return value;
    });
  };

  const handleSelect = (option: CascadeOption, event: any) => {
    const currentEle = event.target;
    if (currentEle) {
      const selectedOptions = getSelectedOptions(options, option.id);
      onChange(selectedOptions.slice(-1)[0], selectedOptions as Option[]);
      const childScrollELe = currentEle.parentNode;
      const parentELe = childScrollELe.parentNode.parentNode;
      const parentScrollEle = parentELe.parentNode;
      const childTop = childScrollELe.scrollTop;
      const parentTop = parentScrollEle.scrollTop;
      const curValue = {
        parent: { ele: parentELe, top: parentTop },
        child: { ele: currentEle, top: childTop },
      };
      handleSelectOption(curValue);
      setAutoCompleteOpen(false);
      hideDropdownMenu();
      inputRef.current?.blur();
    }
  };

  const showDropdownMenu = (): void => {
    setEndAdornment(<ArrowDropUpIcon sx={{ color: '#79747e' }} />);
    selectMenuRef.current?.classList.add(styles.show);
  };

  const hideDropdownMenu = (): void => {
    selectMenuRef.current?.classList.remove(styles.show);
  };

  const isMobileSearch = React.useMemo(() => {
    return isMobile && !!handleMobileSearch;
  }, [isMobile]);

  React.useEffect(() => {
    if (!autoCompleteOpen) {
      hideDropdownMenu();
      inputRef.current?.blur();
    }
  }, [autoCompleteOpen]);

  React.useEffect(() => {
    const selectEle = selectMenuRef.current;
    const {
      parent: { ele: parentEle },
      child: { ele: childEle },
    } = selectOption;
    if (!selectEle || !parentEle || !childEle) {
      return;
    }
    const handleMove = (e: Event) => {
      if (!e.target || !parentEle.contains(e.target as Node)) {
        const childUlEle = childEle.parentNode?.parentNode as HTMLElement;
        childUlEle && childUlEle.classList.remove(SELECT_SHOW);
      }
    };
    selectEle.addEventListener('mouseover', handleMove);
    return () => {
      selectEle.removeEventListener('mousemove', handleMove);
    };
  }, [selectOption]);

  React.useEffect(() => {
    setAutoCompleteOpen(false);
    hideDropdownMenu();
  }, [isMobile]);

  return (
    <div className={`${className} relative`}>
      {label && (
        <label
          className={`block text-sm leading-5.5 font-semibold ${labelColor} mb-2 tablet:mb-3`}
        >
          {label}
        </label>
      )}
      <Autocomplete
        value={value}
        open={autoCompleteOpen}
        renderInput={(params) => (
          <Input
            placeholder={placeholder}
            autoComplete="off"
            inputRef={params.InputProps.ref}
            inputProps={params.inputProps}
            endAdornment={endAdornment}
            myInputRef={inputRef}
            error={error}
            helperText={helperText}
            inputSx={{
              '&:hover': {
                backgroundColor: '#f1f5f9',
              },
              '&::after': {
                display: 'none',
              },
            }}
            readOnly={isMobileSearch}
            onClick={() => {
              if (isMobileSearch) {
                handleMobileSearch?.();
              } else {
                return;
              }
            }}
          />
        )}
        options={searchOptions}
        filterOptions={(options) => options}
        onInputChange={(event, value) => {
          setSearchValue(value.trim());
        }}
        getOptionLabel={(option) => option.name}
        isOptionEqualToValue={(option, value) =>
          !!option &&
          value &&
          !!option?.name &&
          !!value?.name &&
          option.name === value.name
        }
        onChange={(e, item) => {
          const selectedOptions = getSelectedOptions(options, item.id);
          onChange(item, selectedOptions);
          setAutoCompleteOpen(false);
          handleSelectOption(DEFAULT_SELECT_OPTION);
        }}
        readOnly={isMobileSearch}
        data-observer-id={dataObserverId}
        data-ele-group={dataEleGroup}
        data-ele-id={dataEleId}
        onFocus={(e) => {
          if (isMobileSearch) return;
          showDropdownMenu();
          dataEleId && trackClickEvent(e);
          const {
            parent: { ele: parentEle, top: parentTop },
            child: { ele: childEle, top: childTop },
          } = selectOption;
          if (parentEle && childEle) {
            const parentDivEle = parentEle.parentNode as HTMLElement;
            const childDivEle = childEle.parentNode as HTMLElement;
            const childUlEle = parentEle.firstElementChild as HTMLElement;
            if (parentDivEle && childDivEle && childUlEle) {
              parentDivEle.scrollTop = parentTop;
              childDivEle.scrollTop = childTop;
              parentEle.classList.add(SELECT_SELECTED);
              childEle.classList.add(SELECT_SELECTED);
              childUlEle.classList.add(SELECT_SHOW);
            }
          }
        }}
        onInput={() => {
          if (isMobileSearch) return;
          hideDropdownMenu();
          setAutoCompleteOpen(true);
        }}
        onBlur={() => {
          if (isMobileSearch) return;
          hideDropdownMenu();
          setEndAdornment(<ArrowDropDownIcon sx={{ color: '#79747e' }} />);
          setAutoCompleteOpen(false);
        }}
        sx={sx}
        PopperComponent={styledPopper}
        selectOnFocus={false}
        disableClearable={true}
        noOptionsText="抱歉，无此城市的数据"
      />
      <CascadeSelectOptions
        options={options}
        isParentRef={true}
        handleSelect={handleSelect}
        isHalfWidth={isHalfWidth}
        dropdownMenuRef={selectMenuRef}
        className={optionsClassName}
        pageUniqueId={pageUniqueId}
      />
    </div>
  );
};

export default CitySearchOptions;
